import { cloneElement, memo, ReactElement, useCallback, useMemo } from 'react'
import { executeAsync } from 'utils/performance'
import { trackInAmplitude } from '.'
import { AmplitudeEvent } from './amplitude.interface'
import { cloakData, FieldInteraction } from './utils'

export interface InputEventHandlers<T> {
  onChange: (value: T | undefined, raw?: string | number) => void
  onBlur: (value: T | undefined, raw?: string | number) => void
  onFocus: (value: T | undefined, raw?: string | number) => void
}

interface PathComponentProps<T> extends InputEventHandlers<T> {
  path?: string
}

interface Props {
  children: ReactElement<PathComponentProps<unknown>>
  path: string
  trackOnChange?: boolean
  trackFocusEvents?: boolean
}

/**
 * TODO: Refactor into hooks please! :)
 */
const WithFieldInteraction = ({
  children,
  trackOnChange = false,
  trackFocusEvents = true,
  path
}: Props) => {
  const {
    props: { onBlur, onChange, onFocus, ...childProps }
  } = children

  const onBlurHandler = useCallback(
    (value: unknown, raw?: string | number) => {
      if (onBlur) {
        onBlur(value, raw)
      }

      if (trackFocusEvents) {
        executeAsync(() => {
          trackInAmplitude(AmplitudeEvent.InteractWithField, {
            path,
            EventName: FieldInteraction.onBlur,
            value: cloakData(raw || value, path)
          })
        })
      }
    },
    [path, onBlur, trackFocusEvents]
  )
  const onFocusHandler = useCallback(
    (value: unknown, raw?: string | number) => {
      if (onFocus) {
        onFocus(value, raw)
      }

      if (trackFocusEvents) {
        executeAsync(() => {
          trackInAmplitude(AmplitudeEvent.InteractWithField, {
            path,
            EventName: FieldInteraction.onFocus,
            value: cloakData(raw || value, path)
          })
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onFocus, path, trackFocusEvents]
  )
  const onChangeHandler = useCallback(
    (value: unknown, raw?: string | number) => {
      if (onChange) {
        onChange(value, raw)
      }

      if (trackOnChange) {
        executeAsync(() => {
          trackInAmplitude(AmplitudeEvent.InteractWithField, {
            path,
            EventName: FieldInteraction.onChange,
            value: cloakData(raw !== undefined ? raw : value, path)
          })
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange, path, trackOnChange]
  )

  const handlers = useMemo(
    () => ({
      onBlur: onBlurHandler,
      onFocus: onFocusHandler,
      onChange: onChangeHandler
    }),
    [onBlurHandler, onFocusHandler, onChangeHandler]
  )

  return cloneElement(children, {
    ...childProps,
    ...handlers
  })
}

export default memo(WithFieldInteraction)
